import PropTypes from 'prop-types';
import { Component } from 'react';
import Flux from '../../../flux/Flux';
import { openPaytour } from './ProfileLiveShowHelper';
import VXButton from '../../SimpleElements/VXButton/VXButton';
import VXButtonOutline from '../../SimpleElements/VXButton/Presets/VXButtonOutline';
import { COLOR_TYPE_DISABLED, COLOR_TYPE_SPECIAL, COLOR_TYPE_SUCCESS } from '../../SimpleElements/VXButton/ButtonHelper';
import { VXButtonLineConfig } from '../../SimpleElements/VXButton/VXButtonConfig';
import STYLES from '../../SimpleElements/VXButton/EnumVXButton';
import { VXPay } from '../../../utils/VXPay';
import Translations from '../../../utils/Translations';
import GuestAccess from '../../../utils/GuestAccess';
import { formatCurrency, generateKey } from '../../../utils/CommonUtils';
import TicketBoughtModalBox from './TicketBoughtModalBox.js';

class ProfileLiveShowButton extends Component {
	constructor(props) {
		super(props);

		this.state = {
			showModal: false,
			buyResult: {},
		};

		this.buyTicket = this.buyTicket.bind(this);
		this.onBuyClick = this.onBuyClick.bind(this);
		this.onRemoveTicketClick = this.onRemoveTicketClick.bind(this);
		this.startLiveChat = this.startLiveChat.bind(this);
		this.closeModal = this.closeModal.bind(this);
		this.showModal = this.showModal.bind(this);
	}

	buyTicket(onSuccess = null) {
		const start = new Date(this.props.data.start).getTime() / 1000;
		onSuccess =
			typeof onSuccess === 'function'
				? onSuccess
				: (...args) => {
						this.showModal(...args);
					};

		const ticket = {
			modelId: this.props.actorId,
			start,
			promoCode: null,
		};

		Flux.NewMessenger.buyChatTicket(ticket, Flux.Constants.TicketCreationType.CHAT_TICKET);

		const onChatTicketChange = (result) => {
			if (result?.error?.messsage === 'ticket_not_bought') {
				openPaytour(this.props.data.price.value, this.props.data.bonusCode);
			} else if (result?.success) {
				onSuccess(result.success, result?.show?.isBought, result?.show?.isFree);
			}

			Flux.NewMessenger.removeChatTicketChangeListener(onChatTicketChange);
		};

		Flux.NewMessenger.addChatTicketChangeListener(onChatTicketChange);
	}

	onBuyClick() {
		if (!Flux.Guest.isLoggedIn()) {
			if (this.props.data.price.value > 0) {
				Flux.Browser.setLocalItem('triggerBuyTicket', true);
			}

			VXPay.openSignupOrLogin({
				hostData: {
					teaserText: Translations.get('LiveShow'),
				},
			});
		} else if (this.props.data.price.value > 0 && Flux.Guest.getBalance() < this.props.data.price.value && !this.props.data.isBought) {
			openPaytour(this.props.data.price.value, this.props.data.bonusCode);

			VXPay.onSuccess(() => {
				this.buyTicket('', () => {});
			});
		} else {
			this.buyTicket('', () => {});
		}
	}

	onRemoveTicketClick() {
		if (this.props.data.isFree) {
			Flux.NewMessenger.removeChatTicket({ id: this.props.data.id });

			const onChatTicketChange = (result) => {
				if (result?.success) {
					this.showModal(result.success, result?.show?.isBought, result?.show?.isFree);
				}

				Flux.NewMessenger.removeChatTicketChangeListener(onChatTicketChange);
			};

			Flux.NewMessenger.addChatTicketChangeListener(onChatTicketChange);
		}
	}

	startLiveChat(event) {
		if (event.cancelable) {
			event.preventDefault();
		}

		GuestAccess.onLivePreviewAccess(
			() => {
				// auto-buy free show
				if (this.props?.data?.isFree && !this.props?.data?.isBought) {
					this.buyTicket(() => {
						Flux.NewMessenger.startChat(Flux.Constants.VXMessenger.ChatTypes.LIVESHOW, this.props.actorId);
					});
				} else {
					Flux.NewMessenger.startChat(Flux.Constants.VXMessenger.ChatTypes.LIVESHOW, this.props.actorId);
				}
			},
			this.props.actorId,
			false,
		);
	}

	closeModal() {
		this.setState({ showModal: false });
		if (typeof this.props.onCloseModal === 'function') {
			this.props.onCloseModal();
		}
	}

	showModal(success, isBought, isFree) {
		this.setState({ showModal: true, buyResult: { success, isBought, isFree } });
	}

	getContent() {
		const price = formatCurrency(this.props.data.price.value);
		const { isFree, canBuy, isBought } = this.props.data || {};
		const { hasStarted, isShowSite, isFeatured } = this.props;
		const key = generateKey('profile-live-show-button', 1);

		if (isShowSite) {
			return this.getShowSiteButton(key, isFeatured, isBought, hasStarted, isFree, canBuy);
		} else {
			const button = this.getProfileButton(key, isBought, hasStarted, isFree, canBuy, price);

			if (this.props.isHeader) {
				return (
					<div key={key} className={'profile-header-live-show__button'}>
						{button}
					</div>
				);
			}

			return (
				<div key={key} className={this.props.isMobile ? 'h-width-full' : ''}>
					<div className={'profile-live-show__button'}>{button}</div>
				</div>
			);
		}
	}

	getProfileButton(key, isBought, hasStarted, isFree, canBuy, price) {
		const buttonStyle = this.props.isMobile ? STYLES.SIZE.SMALL : (this.props.isHeader ? STYLES.SIZE.MEDIUM : STYLES.SIZE.SMALL);
		const commonButtonProps = {
			key: key,
			size: buttonStyle,
			staticMode: true,
		};
		const canStartOrLogin = hasStarted && (isFree || isBought);

		// start directly or force login
		if (canStartOrLogin) {
			return (
				<VXButton
					{...commonButtonProps}
					color={COLOR_TYPE_SUCCESS}
					line1={[new VXButtonLineConfig(Translations.get('LiveShow'))]}
					line2={[new VXButtonLineConfig(Translations.get(isFree ? 'cam_shows.join_free' : 'cam_shows.join'))]}
					icon={'-icon-webcam-single-full'}
					onClick={this.startLiveChat}
				/>
			);
		}

		// user has already bought a ticket
		if (isFree) {
			return (
				<VXButtonOutline
					{...commonButtonProps}
					line1={[
						new VXButtonLineConfig(Translations.get(isBought ? 'cam_shows.button-remove-reminder' : 'cam_shows.button-set-reminder')),
					]}
					line2={undefined}
					icon={isBought ? '-icon-bell-off-full' : '-icon-bell-full'}
					onClick={isBought ? this.onRemoveTicketClick : this.onBuyClick}
					size={STYLES.SIZE.FULL}
				/>
			);
		}

		// user has no ticket yet but is logged in
		if (Flux.Guest.isLoggedIn()) {
			return (
				<VXButton
					{...commonButtonProps}
					color={canBuy ? COLOR_TYPE_SPECIAL : COLOR_TYPE_DISABLED}
					line1={[new VXButtonLineConfig(Translations.get('cam_shows.buy-ticket').toUpperCase())]}
					line2={[new VXButtonLineConfig(price)]}
					icon={'-icon-voucher-icon'}
					onClick={this.onBuyClick}
					disabled={!canBuy}
				/>
			);
		}
		// user is not logged in
		else {
			return (
				<VXButton
					{...commonButtonProps}
					color={canBuy ? COLOR_TYPE_SPECIAL : COLOR_TYPE_DISABLED}
					line1={[new VXButtonLineConfig(Translations.get('cam_shows.buy-ticket').toUpperCase())]}
					icon={'-icon-voucher-icon-full'}
					onClick={this.onBuyClick}
					disabled={!canBuy}
				/>
			);
		}
	}

	getShowSiteButton(key, isFeatured, isBought, hasStarted, isFree, canBuy) {
		const canStart = hasStarted && (isFree || isBought);

		if (isFeatured) {
			return this.getShowSiteFeaturedButton(isBought, hasStarted, isFree, key, canBuy);
		} else {
			// start show directly
			if (canStart) {
				return (
					<VXButton
						key={key}
						color={COLOR_TYPE_SUCCESS}
						line1={[new VXButtonLineConfig(Translations.get('LiveShow'))]}
						line2={[new VXButtonLineConfig(Translations.get(isFree ? 'cam_shows.join_free' : 'cam_shows.join'))]}
						icon={'-icon-webcam-single-full'}
						onClick={this.startLiveChat}
						staticMode={true}
						size={STYLES.SIZE.SMALL}
					/>
				);
			}

			if (isFree) {
				return (
					<VXButtonOutline
						key={key}
						icon={isBought ? '-icon-bell-off-full' : '-icon-bell-full'}
						line1={[
							new VXButtonLineConfig(Translations.get(isBought ? 'cam_shows.button-remove-reminder' : 'cam_shows.button-set-reminder')),
						]}
						onClick={isBought ? this.onRemoveTicketClick : this.onBuyClick}
						size={STYLES.SIZE.SMALL}
						staticMode={true}
					/>
				);
			}

			return (
				<VXButtonOutline
					key={key}
					line1={[new VXButtonLineConfig(this.props.text)]}
					onClick={this.onBuyClick}
					size={STYLES.SIZE.TINY}
					disabled={!canBuy}
				/>
			);
		}
	}

	getShowSiteFeaturedButton(isBought, hasStarted, isFree, key, canBuy) {
		const canStart = hasStarted && (isFree || isBought);

		// start show directly
		if (canStart) {
			return (
				<VXButton
					key={key}
					color={COLOR_TYPE_SUCCESS}
					line1={[new VXButtonLineConfig(Translations.get('LiveShow'))]}
					line2={[new VXButtonLineConfig(Translations.get(isFree ? 'cam_shows.join_free' : 'cam_shows.join'))]}
					icon={'-icon-webcam-single-full'}
					onClick={this.startLiveChat}
					size={STYLES.SIZE.SMALL}
					staticMode={true}
				/>
			);
		}

		if (isFree) {
			return (
				<VXButtonOutline
					key={key}
					icon={isBought ? '-icon-bell-off-full' : '-icon-bell-full'}
					line1={[
						new VXButtonLineConfig(Translations.get(isBought ? 'cam_shows.button-remove-reminder' : 'cam_shows.button-set-reminder')),
					]}
					onClick={isBought ? this.onRemoveTicketClick : this.onBuyClick}
					size={STYLES.SIZE.SMALL}
					staticMode={true}
				/>
			);
		}

		return (
			<VXButton
				key={key}
				color={canBuy ? COLOR_TYPE_SPECIAL : COLOR_TYPE_DISABLED}
				line1={[new VXButtonLineConfig(this.props.text)]}
				onClick={this.onBuyClick}
				size={STYLES.SIZE.SMALL}
				staticMode={true}
				disabled={!canBuy}
			/>
		);
	}

	render() {
		return (
			<>
				{this.state.showModal && <TicketBoughtModalBox key="modal" closeModal={this.closeModal} buyResult={this.state.buyResult} />}
				{!this.props.children && this.getContent()}
				{this.props.children}
			</>
		);
	}
}

ProfileLiveShowButton.propTypes = {
	children: PropTypes.node,
	data: PropTypes.object,
	hasStarted: PropTypes.bool,
	actorId: PropTypes.number,
	isMobile: PropTypes.bool,
	isHeader: PropTypes.bool,
	onCloseModal: PropTypes.func,
	isShowSite: PropTypes.bool,
	isFeatured: PropTypes.bool,
	text: PropTypes.string,
};

ProfileLiveShowButton.defaultProps = {
	data: null,
	hasStarted: false,
	isMobile: false,
	isHeader: false,
	isShowSite: false,
	isFeatured: false,
	onCloseModal: null,
	text: '',
};

export default ProfileLiveShowButton;
